import { useCallback, useEffect, useRef, useState } from "react";
import { startPhone, confirmPhone as confirmPhoneApi } from "../../../services/mfa/mfaApi";
import { normaliseCode } from "./mfaCodeFormat";
import useCountdown from "./useCountdown";

const INVALID_FORMAT = "Enter the 6-digit code.";
const TIMED_OUT = "Your sign-in timed out.";

// A verify answer that is a setup step, not a session (docs/specs/authenticator-mfa-api.md, "Login setup").
export const isSetupAnswer = (res) =>
  Array.isArray(res?.setup_required) && res.setup_required.length > 0 && !res.session;

// Login setup (docs/specs/login-setup.md §2): after the code, the phone step then the authenticator step, in place
// on the login screen. Lives beside useMfaLogin, which owns the tx (`txRef`), the busy guard (`run`), the stage and
// the error line. M in "Step N of M" is the first `setup_required` seen in this sign-in; N counts up from it.
// The typed number is sent and forgotten: it is never kept here, stored or logged.
const useLoginSetup = ({ run, txRef, setStage, setError, beginEnrollment, onSession }) => {
  const [step, setStep] = useState({ n: 0, total: 0 });
  const [expiresAt, setExpiresAt] = useState(null);
  const [phoneOnFile, setPhoneOnFile] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  const [sendBlocked, setSendBlocked] = useState(false);
  const totalRef = useRef(0);
  const phoneConfirmedRef = useRef(false);
  const secondsLeft = useCountdown(expiresAt);

  useEffect(() => {
    // useCountdown still reads 0 on the render that sets a new deadline: check the deadline itself too.
    if (!expiresAt || secondsLeft > 0 || Date.now() < expiresAt) return;
    setExpiresAt(null);
    setError(TIMED_OUT);
    setStage("expired");
  }, [expiresAt, secondsLeft, setError, setStage]);

  const handle = useCallback((res) => {
    const remaining = res.setup_required.length;
    totalRef.current = totalRef.current || remaining;
    const total = totalRef.current;
    const n = Math.min(total, Math.max(1, total - remaining + 1));
    setStep({ n, total });
    if (res.setup_expires_in > 0) setExpiresAt(Date.now() + res.setup_expires_in * 1000);
    const stepLabel = total > 1 ? `Step ${n} of ${total}` : null;
    if (res.next_step === "phone") {
      setPhoneOnFile(res.phone_on_file_masked || null);
      setSentTo(null);
      setSendBlocked(false);
      setStage("phone");
      return;
    }
    if (res.next_step === "totp") {
      beginEnrollment(res, { setup: true, stepLabel, skipMobileStep: phoneConfirmedRef.current });
      return;
    }
    throw new Error(`login setup: unknown next_step "${res.next_step}"`);
  }, [setStage, beginEnrollment]);

  // `digits` null = the number on file. Past tx_max_sends Oracle answers METHOD_UNAVAILABLE: no more sends.
  const sendPhone = useCallback(
    (digits) =>
      run(async () => {
        try {
          const res = await startPhone({ mfaTx: txRef.current, phone: digits });
          setSentTo(res?.phone_masked || phoneOnFile);
        } catch (e) {
          if (e?.response?.data?.error?.code !== "METHOD_UNAVAILABLE") throw e;
          setSendBlocked(true);
        }
      }),
    [run, txRef, phoneOnFile],
  );

  const confirmPhone = useCallback(
    (raw) =>
      run(async () => {
        const code = normaliseCode("sms", raw);
        if (!code) return setError(INVALID_FORMAT);
        const res = await confirmPhoneApi({ mfaTx: txRef.current, code });
        phoneConfirmedRef.current = true;
        if (res?.session) return onSession(res.session);
        if (!isSetupAnswer(res)) throw new Error("phone-confirm returned no session and no setup step");
        handle(res);
      }),
    [run, txRef, setError, onSession, handle],
  );

  const changeNumber = useCallback(() => {
    setSentTo(null);
    setError(null);
  }, [setError]);

  const reset = useCallback(() => {
    totalRef.current = 0;
    phoneConfirmedRef.current = false;
    setStep({ n: 0, total: 0 });
    setExpiresAt(null);
    setPhoneOnFile(null);
    setSentTo(null);
    setSendBlocked(false);
  }, []);

  return {
    stepLabel: step.total > 1 ? `Step ${step.n} of ${step.total}` : null,
    expiresAt, phoneOnFile, sentTo, sendBlocked,
    handle, sendPhone, confirmPhone, changeNumber, reset,
  };
};

export default useLoginSetup;
